import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Scale, Plus, Search, X, RefreshCcw, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, Activity, ChevronDown, ChevronUp, Download, Upload, FileText, FileSpreadsheet, FileOutput, List, LayoutList, Filter, LayoutGrid, Layers, Calendar, XCircle, Clock } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import { mapComptabiliteStatusOptions, useComptabiliteTableColumnVisibility, useComptabiliteListRowSelection, ComptabiliteBulkDeleteFilterAction, COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS } from './utils/comptabiliteListPageUtils';
import ComptabiliteFiscalYearField from './components/ComptabiliteFiscalYearField';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule } from '../../shared/utils/columnHeaderFilters';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import {
  getBalances,
  createBalance,
  updateBalance,
  deleteBalance,
  getChartOfAccounts,
} from '../../shared/utils/comptabiliteService';
import { CustomDatePicker, CustomSelect, CustomNumberInput } from '../../shared/components/custom';
import {
  EMPTY_BALANCE_FORM,
  buildBalancePayload,
  recordToFormData,
} from './03-balance-generale/balanceGeneralePayload';
import {
  formatComptabiliteMoney,
} from './utils/comptabiliteSaudiFormat';
import '../../core/styles/global-dropdowns.css';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useSettings } from '../../core/contexts/SettingsContext';
import { useComptabiliteConfig } from './useComptabiliteConfig';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';
import { buildChartAccountOptions, CHART_ACCOUNTS_REFRESH_EVENT } from './utils/chartAccountOptions';
import { fetchCurrentFiscalYearContext, resolveFiscalYearLabel } from './utils/fiscalYearUtils';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
} from '../../products/saudi/components/QawaemTableSelection';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';

const RequiredLabel = ({ children }) => (
  <>
    <span className="text-red-400 ml-1">*</span>
    {children}
  </>
);

const BALANCE_LIST_COLUMNS = [
  { key: 'balance_ref', labelKey: 'th_reference' },
  { key: 'numero_compte', labelKey: 'th_account_number' },
  { key: 'libelle', labelKey: 'th_label' },
  { key: 'solde_net', labelKey: 'th_net_balance' },
  { key: 'statut_balance', labelKey: 'th_status' },
];

const BalanceGeneralePage = () => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  useComptabiliteConfig();
  const { isDashboardLightMode } = useGlobalTheme();
  const p = (key, opts) => t(`modules.comptabilite.pages.balanceGeneralePage.${key}`, opts);
  const fmtMoney = (amount) => formatComptabiliteMoney(amount);

  const translateBalanceStatus = useCallback((status) => {
    const map = {
      'Validé': p('status_validated'),
      'Définitif': p('status_definitive'),
      'Provisoire': p('status_provisional') };
    return map[status] || status;
  }, [t]);

  const translateBalanceType = useCallback((type) => {
    const map = {
      'Avant Clôture': p('type_before_closing'),
      'Après Clôture': p('type_after_closing'),
      'Définitive': p('type_definitive') };
    return map[type] || type;
  }, [t]);

  const translateBalanceSense = useCallback((sense) => {
    const map = {
      'Débiteur': p('sense_debit'),
      'Créditeur': p('sense_credit'),
      'Nul': p('sense_null') };
    return map[sense] || sense || p('not_available');
  }, [t]);

  // State Management
  const [balances, setBalances] = useState([]);
  const [chartAccounts, setChartAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBalance, setSelectedBalance] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  
  // Advanced Options
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_balances');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdowns
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Selection & column filters
  const { filters: columnFilters, setFilter: setColumnFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  
  // Expanded sections for GPS-style collapsible form
  const [balanceGeneraleFormTab, setBalanceGeneraleFormTab] = useState('balanceInfo');
  const balanceGeneraleFormTabs = useMemo(() => ([
    { id: 'balanceInfo', label: p('section_balance_info'), icon: ChevronUp },
    { id: 'soldesMovements', label: p('section_balances_movements'), icon: ChevronUp },
    { id: 'controlesNotes', label: p('section_comments'), icon: ChevronUp }
  ]), [p]);

  
  const [formData, setFormData] = useState({ ...EMPTY_BALANCE_FORM, balance_ref: '' });
  const [fiscalYearLabel, setFiscalYearLabel] = useState('');
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  
  useEffect(() => {
    loadBalances();
    loadChartAccounts();
    fetchCurrentFiscalYearContext()
      .then(({ label }) => { if (label) setFiscalYearLabel(label); })
      .catch(() => {});
  }, []);

  const loadChartAccounts = () => {
    getChartOfAccounts()
      .then((rows) => setChartAccounts(Array.isArray(rows) ? rows : []))
      .catch((error) => {
        console.error('Error loading chart accounts:', error);
        setChartAccounts([]);
      });
  };

  useEffect(() => {
    const onRefresh = () => { loadChartAccounts(); };
    window.addEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(CHART_ACCOUNTS_REFRESH_EVENT, onRefresh);
  }, []);
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const loadBalances = async () => {
    try {
      setLoading(true);
      const response = await getBalances();
      setBalances(sortRecordsNewestFirst(Array.isArray(response) ? response : []));
    } catch (error) {
      console.error('Error loading balances:', error);
      setBalances([]);
    } finally {
      setLoading(false);
    }
  };
  
  const columnFilterRules = useMemo(() => [
    textColumnRule('balance_ref', (row) => row.balance_ref ?? ''),
    textColumnRule('numero_compte', (row) => row.numero_compte ?? ''),
    textColumnRule('solde_net', (row) => String(row.solde_net ?? '')),
  ], []);

  // Select handlers
  
  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => deleteBalance(id)));
        notifyBulkDeleted(showNotify, deleteCount, p('entity_label_plural', { defaultValue: 'الأرصدة' }));
        setSelectedRecords([]);
        clearSelection();
        loadBalances();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      }
    }
  };
  
  const generateBalanceReference = useCallback(() => {
    const year = new Date().getFullYear();
    const prefix = getEntityPrefix(settings, 'balance', 'BALG');
    return generateEntityReference(`${prefix}-${year}`, balances, (row) => row.balance_ref);
  }, [balances, settings]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = buildBalancePayload(formData);
      if (!selectedBalance) {
        payload.balance_ref = (formData.balance_ref || '').trim() || generateBalanceReference();
      }
      
      if (selectedBalance) {
        await updateBalance(selectedBalance.id, payload);
        notifyUpdated(showNotify, p('entity_label', { defaultValue: 'الرصيد' }));
      } else {
        await createBalance(payload);
        notifyCreated(showNotify, p('entity_label', { defaultValue: 'الرصيد' }));
      }
      
      await loadBalances();
      resetForm();
    } catch (error) {
      console.error('Error saving balance:', error);
      notifyError(showNotify, error.message || p('save_error'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleEdit = (balance) => {
    setSelectedBalance(balance);
    setFormData({ ...recordToFormData(balance), balance_ref: balance.balance_ref || '' });
    setBalanceGeneraleFormTab('balanceInfo');
    setShowModal(true);
  };
  
  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await deleteBalance(id);
        notifyDeleted(showNotify, p('entity_label', { defaultValue: 'الرصيد' }));
        await loadBalances();
      } catch (error) {
        console.error('Error deleting balance:', error);
        notifyError(showNotify, p('save_error', { message: error.message }));
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleView = (balance) => {
    setSelectedBalance(balance);
    setShowViewModal(true);
  };

  const accountOptions = useMemo(
    () => buildChartAccountOptions(chartAccounts),
    [chartAccounts],
  );

  const handleAccountNumberChange = useCallback((numero) => {
    const acc = chartAccounts.find((a) => a.account_number === numero);
    setFormData((prev) => ({
      ...prev,
      numero_compte: numero,
      libelle_compte: acc?.account_name || prev.libelle_compte,
      compte_classe: acc?.class_number != null ? String(acc.class_number) : prev.compte_classe,
    }));
  }, [chartAccounts]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      const balance = balances.find((b) => b.id === recordId);
      if (!balance) return;
      await updateBalance(recordId, { ...balance, statut_balance: newStatus });
      loadBalances();
    } catch (error) {
      console.error('Error updating balance status:', error);
    }
  };
  
  const resetForm = () => {
    setFormData({ ...EMPTY_BALANCE_FORM, balance_ref: '' });
    setSelectedBalance(null);
    setShowModal(false);
    setBalanceGeneraleFormTab('balanceInfo');
  };

  const openNewBalanceModal = () => {
    setSelectedBalance(null);
    setFormData({
      ...EMPTY_BALANCE_FORM,
      balance_ref: generateBalanceReference(),
      exercice_fiscal: fiscalYearLabel || resolveFiscalYearLabel(),
    });
    setBalanceGeneraleFormTab('balanceInfo');
    setShowModal(true);
  };
  
  // Stats
  const stats = {
    totalComptes: balances.length,
    soldeDebiteur: balances.reduce((sum, b) => sum + (parseFloat(b.solde_debiteur_fin) || 0), 0),
    soldeCrediteur: balances.reduce((sum, b) => sum + (parseFloat(b.solde_crediteur_fin) || 0), 0),
    balanceEquilibree: balances.filter(b => Math.abs((parseFloat(b.solde_debiteur_fin) || 0) - (parseFloat(b.solde_crediteur_fin) || 0)) < 0.01).length,
    anomalies: balances.filter(b => b.anomalies_detectees).length
  };
  
  // Distribution data
  const balanceDistribution = useMemo(() => ({
    all_balances: {
      title: p('dist_overview_title'),
      data: [
        {
          id: 'active_accounts',
          name: p('dist_active_accounts'),
          count: balances.filter(b => b.compte_actif).length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          id: 'passive_accounts',
          name: p('dist_passive_accounts'),
          count: balances.filter(b => b.compte_passif).length,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        },
        {
          id: 'expense_accounts',
          name: p('dist_expense_accounts'),
          count: balances.filter(b => b.compte_charge).length,
          color: 'bg-gradient-to-r from-orange-500 to-orange-600'
        },
        {
          id: 'revenue_accounts',
          name: p('dist_revenue_accounts'),
          count: balances.filter(b => b.compte_produit).length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600'
        }
      ]
    },
    by_type: {
      title: p('dist_by_type_title'),
      data: [
        {
          id: 'Avant Clôture',
          name: p('dist_before_closing'),
          count: balances.filter(b => b.type_balance === 'Avant Clôture').length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          id: 'Après Clôture',
          name: p('dist_after_closing'),
          count: balances.filter(b => b.type_balance === 'Après Clôture').length,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        },
        {
          id: 'Définitive',
          name: p('dist_definitive'),
          count: balances.filter(b => b.type_balance === 'Définitive').length,
          color: 'bg-gradient-to-r from-purple-500 to-purple-600'
        }
      ]
    },
    by_status: {
      title: p('dist_by_status_title'),
      data: [
        {
          id: 'Provisoire',
          name: p('dist_status_provisional'),
          count: balances.filter(b => b.statut_balance === 'Provisoire').length,
          color: 'bg-gradient-to-r from-yellow-500 to-yellow-600'
        },
        {
          id: 'Définitif',
          name: p('dist_status_definitive'),
          count: balances.filter(b => b.statut_balance === 'Définitif').length,
          color: 'bg-gradient-to-r from-blue-500 to-blue-600'
        },
        {
          id: 'Validé',
          name: p('dist_status_validated'),
          count: balances.filter(b => b.statut_balance === 'Validé').length,
          color: 'bg-gradient-to-r from-green-500 to-green-600'
        }
      ]
    }
  }), [balances, t]);

  const distributionInfo = balanceDistribution[mainViewMode] || balanceDistribution.all_balances;

  // Main view tabs
  const mainTabs = useMemo(() => [
    { id: 'all_balances', label: p('tab_general'), icon: LayoutGrid },
    { id: 'by_type', label: p('tab_by_type'), icon: Scale },
    { id: 'by_status', label: p('tab_by_status'), icon: CheckCircle }
  ], [t]);

  // Quick filter buttons
  const quickFilterButtons = useMemo(() => [
    { id: 'all', label: p('filter_all'), icon: Layers },
    { id: 'Validé', label: p('filter_validated'), icon: CheckCircle },
    { id: 'Provisoire', label: p('filter_provisional'), icon: AlertTriangle },
    { id: 'anomalies', label: p('filter_anomalies'), icon: AlertTriangle }
  ], [t]);

  const statCards = useMemo(() => [
    { label: p('stat_total_accounts'), value: stats.totalComptes, icon: Scale, color: 'from-purple-500 to-purple-700' },
    { label: p('stat_debit_balance'), value: fmtMoney(stats.soldeDebiteur), icon: TrendingUp, color: 'from-green-500 to-green-600' },
    { label: p('stat_credit_balance'), value: fmtMoney(stats.soldeCrediteur), icon: TrendingDown, color: 'from-blue-500 to-blue-600' },
    { label: p('stat_balanced'), value: stats.balanceEquilibree, icon: CheckCircle, color: 'from-emerald-500 to-emerald-600' },
    { label: p('stat_anomalies'), value: stats.anomalies, icon: AlertTriangle, color: 'from-red-500 to-red-600' }
  ], [stats, t]);

  const qawaemStats = useMemo(() => sliceQawaemStats(
    statCards.slice(0, 4).map((s, i) => ({
      key: s.label,
      label: s.label,
      value: s.value,
      icon: s.icon,
      variant: i % 2 ? 'forest' : 'copper' })),
    4,
  ), [statCards]);

  const quickFilterLabel = quickFilterButtons.find((b) => b.id === quickFilter)?.label || p('filter_all');

  const typeBalanceOptions = useMemo(() => [
    { value: 'Avant Clôture', label: p('type_before_closing'), isDefault: true },
    { value: 'Après Clôture', label: p('type_after_closing'), isDefault: true },
    { value: 'Définitive', label: p('type_definitive'), isDefault: true }
  ], [t]);

  const statusBalanceOptions = useMemo(() => [
    { value: 'Provisoire', label: p('status_provisional'), isDefault: true },
    { value: 'Définitif', label: p('status_definitive'), isDefault: true },
    { value: 'Validé', label: p('status_validated'), isDefault: true }
  ], [t]);

  const statusBadgeOptions = useMemo(() => mapComptabiliteStatusOptions([
    { value: 'Provisoire', labelKey: 'status_provisional', icon: AlertTriangle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'Définitif', labelKey: 'status_definitive', icon: CheckCircle, style: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { value: 'Validé', labelKey: 'status_validated', icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
  ], p), [t]);

  const senseOptions = useMemo(() => [
    { value: 'Débiteur', label: p('sense_debit'), isDefault: true },
    { value: 'Créditeur', label: p('sense_credit'), isDefault: true },
    { value: 'Nul', label: p('sense_null'), isDefault: true }
  ], [t]);
  
  const filteredBalances = useMemo(() => {
    const base = balances.filter(balance => {

      if (quickFilter === 'anomalies' && !balance.anomalies_detectees) return false;
      if (quickFilter !== 'all' && quickFilter !== 'anomalies' && balance.statut_balance !== quickFilter) return false;

      if (activeDistributionFilter) {
        if (mainViewMode === 'by_status') {
          if (balance.statut_balance !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'by_type') {
          if (balance.type_balance !== activeDistributionFilter) return false;
        } else if (mainViewMode === 'all_balances') {
          if (activeDistributionFilter === 'active_accounts' && !balance.compte_actif) return false;
          if (activeDistributionFilter === 'passive_accounts' && !balance.compte_passif) return false;
          if (activeDistributionFilter === 'expense_accounts' && !balance.compte_charge) return false;
          if (activeDistributionFilter === 'revenue_accounts' && !balance.compte_produit) return false;
        }
      }

      const matchesSearch = !searchTerm ||
        balance.balance_ref?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        balance.numero_compte?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        balance.libelle_compte?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        balance.exercice_fiscal?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });
    return applyColumnFilters(base, columnFilters, columnFilterRules);
  }, [balances, quickFilter, activeDistributionFilter, mainViewMode, searchTerm, columnFilters, columnFilterRules]);

  const {
    selectedRecords: selectedRecords,
    setSelectedRecords: setSelectedRecords,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectRecordBase,
    clearSelection,
  } = useComptabiliteListRowSelection(filteredBalances, (row) => row.id);
  const handleSelectRecord = handleSelectRecordBase;
  
  

  const {
    qawaemColumnDefs,
    handleToggleColumn,
    qawaemVisibleColumnCount,
    isColVisible,
    visibleDataColumnCount } = useComptabiliteTableColumnVisibility(BALANCE_LIST_COLUMNS, p);
  const balanceTableColSpan = visibleDataColumnCount + 2;
  
  return (
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Scale}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={(
        <ComptabiliteBulkDeleteFilterAction
          count={selectedRecords.length}
          onClick={handleBulkDelete}
          label={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
          title={p('delete_with_count', { count: selectedRecords.length, defaultValue: `حذف (${selectedRecords.length})` })}
        />
      )}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openNewBalanceModal}
      createLabel={p('new_balance')}
      headerActions={(
        <>
          <button type="button" onClick={loadBalances} className="refresh-button" aria-label={p('refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
          <button
            type="button"
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
          </button>
        </>
      )}
    >
      {/* Advanced Options Section */}
      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            {/* Main View Mode Tabs */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setMainViewMode(tab.id); setActiveDistributionFilter(null); }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quick Filters */}
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Distribution Overview */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white/80 text-sm font-semibold uppercase tracking-wider">{distributionInfo.title}</h3>
                {activeDistributionFilter && (
                  <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                    <XCircle size={14} />{p('reset_filter')}
                  </button>
                )}
              </div>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => (
                  <button key={item.id || item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === (item.id || item.name) ? null : (item.id || item.name))} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === (item.id || item.name) ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className={`font-medium ${activeDistributionFilter === (item.id || item.name) ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                      <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                    </div>
                    <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                      <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                        const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                        return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                      })()}%` }}></div>
                    </div>
                    <p className={`mt-2 text-sm ${activeDistributionFilter === (item.id || item.name) ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })() })}</p>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main Content */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <QawaemTableSelectAllHeader
                    allRowsSelected={allRowsSelected}
                    someRowsSelected={someRowsSelected}
                    onChange={handleSelectAll}
                  />
                  {isColVisible('balance_ref') ? (
                    <TableColumnHeaderFilter columnKey="balance_ref" label={p('th_reference')} value={columnFilters.balance_ref || ''} onChange={(v) => setColumnFilter('balance_ref', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('numero_compte') ? (
                    <TableColumnHeaderFilter columnKey="numero_compte" label={p('th_account_number')} value={columnFilters.numero_compte || ''} onChange={(v) => setColumnFilter('numero_compte', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('libelle') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('th_label')}</th>
                  ) : null}
                  {isColVisible('solde_net') ? (
                    <TableColumnHeaderFilter columnKey="solde_net" label={p('th_net_balance')} value={columnFilters.solde_net || ''} onChange={(v) => setColumnFilter('solde_net', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('statut_balance') ? (
                    <th className="px-6 py-4 text-center text-white font-semibold">{p('th_status')}</th>
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('th_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                  thClassName={COMPTABILITE_TABLE_HEADER_ACTIONS_CLASS}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredBalances.length > 0 ? (
                  filteredBalances.map((balance, index) => (
                    <tr 
                      key={balance.id}
                      className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(balance.id) ? 'bg-white/10' : ''}`}
                    >
                      <QawaemTableSelectRowCell
                        checked={selectedRecords.includes(balance.id)}
                        onChange={() => handleSelectRecord(balance.id)}
                      />
                      {isColVisible('balance_ref') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{balance.balance_ref}</span>
                          <span className="text-white/50 text-xs mt-0.5">{balance.exercice_fiscal}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('numero_compte') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{balance.numero_compte}</span>
                          <span className="text-white/50 text-xs mt-0.5">{balance.compte_classe}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('libelle') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm">{balance.libelle_compte}</span>
                      </td>
                      ) : null}
                      {isColVisible('solde_net') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className={`font-semibold ${parseFloat(balance.solde_net) > 0 ? 'text-green-400' : parseFloat(balance.solde_net) < 0 ? 'text-red-400' : 'text-white'}`}>
                            {fmtMoney(parseFloat(balance.solde_net || 0))}
                          </span>
                          <span className="text-white/50 text-xs mt-0.5">{translateBalanceSense(balance.sens_solde)}</span>
                        </div>
                      </td>
                      ) : null}
                      {isColVisible('statut_balance') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={balance.statut_balance || 'Provisoire'}
                            recordId={balance.id}
                            options={statusBadgeOptions}
                            onChange={handleStatusChange}
                            title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(balance)}
                          onEdit={() => handleEdit(balance)}
                          onDelete={() => handleDelete(balance.id)}
                          viewTitle={t('common.view')}
                          editTitle={t('common.edit')}
                          deleteTitle={t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={balanceTableColSpan} className="text-center py-12 text-white/60">
                      <Scale size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Cards View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredBalances.length > 0 ? (
              filteredBalances.map((balance, index) => (
                <motion.div
                  key={balance.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Scale size={20} className="text-purple-400" />
                          {balance.balance_ref}
                        </h3>
                        <QawaemClickableStatusBadge
                          status={balance.statut_balance || 'Provisoire'}
                          recordId={balance.id}
                          options={statusBadgeOptions}
                          onChange={handleStatusChange}
                          title={p('click_change_status') || t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_account_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_account_number')}</span> {balance.numero_compte}</div>
                            <div><span className="font-medium">{p('label_label')}</span> {balance.libelle_compte}</div>
                            <div><span className="font-medium">{p('label_class')}</span> {balance.compte_classe}</div>
                            <div><span className="font-medium">{p('label_exercise')}</span> {balance.exercice_fiscal}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_balances')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_debit')}</span> <span className="text-green-400">{fmtMoney(parseFloat(balance.solde_debiteur_fin || 0))}</span></div>
                            <div><span className="font-medium">{p('label_credit')}</span> <span className="text-blue-400">{fmtMoney(parseFloat(balance.solde_crediteur_fin || 0))}</span></div>
                            <div><span className="font-medium">{p('label_net_balance')}</span> <span className={`font-bold ${parseFloat(balance.solde_net) > 0 ? 'text-green-400' : parseFloat(balance.solde_net) < 0 ? 'text-red-400' : 'text-white'}`}>{fmtMoney(parseFloat(balance.solde_net || 0))}</span></div>
                            <div><span className="font-medium">{p('label_sense')}</span> {translateBalanceSense(balance.sens_solde)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_movements')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_total_debit')}</span> {fmtMoney(parseFloat(balance.total_mouvements_debit || 0))}</div>
                            <div><span className="font-medium">{p('label_total_credit')}</span> {fmtMoney(parseFloat(balance.total_mouvements_credit || 0))}</div>
                            <div><span className="font-medium">{p('label_entry_count')}</span> {balance.nombre_ecritures || 0}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_validation')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_type')}</span> {translateBalanceType(balance.type_balance)}</div>
                            <div><span className="font-medium">{p('label_validated')}</span> {balance.validation_comptable ? p('yes') : p('no')}</div>
                            {balance.anomalies_detectees && (
                              <div className="mt-2 p-2 bg-red-500/10 rounded border border-red-500/30">
                                <span className="text-red-400 text-xs font-medium">{p('label_anomalies_detected')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => handleView(balance)}
                      onEdit={() => handleEdit(balance)}
                      onDelete={() => handleDelete(balance.id)}
                      viewTitle={t('common.view')}
                      editTitle={t('common.edit')}
                      deleteTitle={t('common.delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Scale size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Add/Edit Modal - GPS Style with 4 Collapsible Sections */}
      {showModal && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={resetForm} role="presentation">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onClick={(e) => e.stopPropagation()} className={`backdrop-blur-lg rounded-2xl max-h-[90vh] flex flex-col overflow-hidden ${isDashboardLightMode ? 'qawaem-modal-light border' : 'border-2 border-blue-400/60 bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800'}`} style={isDashboardLightMode ? { boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)', width: '90%', maxWidth: '1200px' } : { boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className={`flex items-center justify-between p-6 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <h2 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'} flex items-center gap-2`}>
                <Scale className="h-6 w-6 text-purple-400" />
                {selectedBalance ? p('modal_edit_title') : p('modal_add_title')}
              </h2>
              <div className="flex items-center gap-3">
                <button 
                  type="submit" 
                  form="balance-form"
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"
                >
                  <CheckCircle size={18} />
                  {selectedBalance ? p('update') : p('create')}
                </button>
                <button onClick={resetForm} className={isDashboardLightMode ? 'text-gray-500 hover:text-gray-800 transition-colors' : 'text-white/70 hover:text-white transition-colors'}>
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`shrink-0 px-6 pt-3 border-b ${isDashboardLightMode ? 'border-gray-200' : 'border-white/10'}`}>
              <div className="flex flex-wrap gap-1" role="tablist">
                {balanceGeneraleFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = balanceGeneraleFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setBalanceGeneraleFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? (isDashboardLightMode ? 'bg-[#6e9b6e]/15 text-forest-900 border-b-2 border-[#6e9b6e] -mb-px' : 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px') : (isDashboardLightMode ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/80' : 'text-white/55 hover:text-white hover:bg-white/5')}`}>
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="balance-form" onSubmit={handleSubmit}>
                {balanceGeneraleFormTab === 'balanceInfo' && (
<div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')}</label>
                            <input
                              type="text"
                              value={formData.balance_ref}
                              onChange={(e) => setFormData({ ...formData, balance_ref: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_reference')}
                            />
                          </div>
<ComptabiliteFiscalYearField label={p('label_fiscal_year')} value={formData.exercice_fiscal} onChange={(val) => setFormData({ ...formData, exercice_fiscal: val })} placeholder={p('placeholder_fiscal_year')} required />
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_period_start')}</label>
                            <CustomDatePicker
                              value={formData.periode_debut}
                              onChange={(dateString) => setFormData({ ...formData, periode_debut: dateString })}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_period_end')}</label>
                            <CustomDatePicker
                              value={formData.periode_fin}
                              onChange={(dateString) => setFormData({ ...formData, periode_fin: dateString })}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_establishment_date')}</label>
                            <CustomDatePicker
                              value={formData.date_etablissement}
                              onChange={(dateString) => setFormData({ ...formData, date_etablissement: dateString })}
                              placeholder={p('select')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_balance_type')}</label>
                            <CustomSelect
                              value={formData.type_balance}
                              onChange={(val) => setFormData({ ...formData, type_balance: val })}
                              options={typeBalanceOptions}
                              placeholder={p('select')}
                              searchable={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_balance_status')}</label>
                            <CustomSelect
                              value={formData.statut_balance}
                              onChange={(val) => setFormData({ ...formData, statut_balance: val })}
                              options={statusBalanceOptions}
                              placeholder={p('select')}
                              searchable={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_number_form')}</RequiredLabel></label>
                            <CustomSelect
                              value={formData.numero_compte}
                              onChange={handleAccountNumberChange}
                              options={accountOptions}
                              placeholder={p('placeholder_account_number')}
                              searchable
                              creatable
                              autoSelect={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_label')}</RequiredLabel></label>
                            <input
                              type="text"
                              required
                              value={formData.libelle_compte}
                              onChange={(e) => setFormData({ ...formData, libelle_compte: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_account_label')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabel>{p('label_account_class')}</RequiredLabel></label>
                            <input
                              type="text"
                              required
                              value={formData.compte_classe}
                              onChange={(e) => setFormData({ ...formData, compte_classe: e.target.value })}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_account_class')}
                            />
                          </div>
                  </div>
                </div>
                )}
                {balanceGeneraleFormTab === 'soldesMovements' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
<div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_debit_start')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.solde_debiteur_debut}
                              onChange={(v) => setFormData({ ...formData, solde_debiteur_debut: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_credit_start')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.solde_crediteur_debut}
                              onChange={(v) => setFormData({ ...formData, solde_crediteur_debut: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_debit_movements')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.total_mouvements_debit}
                              onChange={(v) => setFormData({ ...formData, total_mouvements_debit: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_total_credit_movements')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.total_mouvements_credit}
                              onChange={(v) => setFormData({ ...formData, total_mouvements_credit: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_debit_end')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.solde_debiteur_fin}
                              onChange={(v) => setFormData({ ...formData, solde_debiteur_fin: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_credit_end')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.solde_crediteur_fin}
                              onChange={(v) => setFormData({ ...formData, solde_crediteur_fin: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_net_balance_form')}</label>
                            <CustomNumberInput
                              step={0.01}
                              value={formData.solde_net}
                              onChange={(v) => setFormData({ ...formData, solde_net: v })}
                              placeholder={p('placeholder_amount')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_balance_sense')}</label>
                            <CustomSelect
                              value={formData.sens_solde}
                              onChange={(val) => setFormData({ ...formData, sens_solde: val })}
                              options={senseOptions}
                              placeholder={p('select')}
                              searchable={false}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_entry_count_form')}</label>
                            <CustomNumberInput
                              step={1}
                              value={formData.nombre_ecritures}
                              onChange={(v) => setFormData({ ...formData, nombre_ecritures: v })}
                              placeholder={p('placeholder_zero')}
                            />
                          </div>
</div>
                )}
                {balanceGeneraleFormTab === 'controlesNotes' && (
                  <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.compte_actif}
                                onChange={(e) => setFormData({ ...formData, compte_actif: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_active_account')}
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.compte_passif}
                                onChange={(e) => setFormData({ ...formData, compte_passif: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_passive_account')}
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.compte_charge}
                                onChange={(e) => setFormData({ ...formData, compte_charge: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_expense_account')}
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.compte_produit}
                                onChange={(e) => setFormData({ ...formData, compte_produit: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_revenue_account')}
                            </label>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.validation_comptable}
                                onChange={(e) => setFormData({ ...formData, validation_comptable: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_accounting_validation')}
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.conformite_pcn}
                                onChange={(e) => setFormData({ ...formData, conformite_pcn: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_pcn_compliance')}
                            </label>
                            <label className="flex items-center gap-2 text-white/70 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.rapprochements_effectues}
                                onChange={(e) => setFormData({ ...formData, rapprochements_effectues: e.target.checked })}
                                className="rounded border-white/20 bg-white/10 text-blue-500 focus:ring-blue-500"
                              />
                              {p('checkbox_reconciliations_done')}
                            </label>
                          </div>
                    <div className="pt-6 border-t border-white/10">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_account_comments')}</label>
                            <textarea
                              value={formData.commentaires_compte}
                              onChange={(e) => setFormData({ ...formData, commentaires_compte: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_account_comments')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_verification_notes')}</label>
                            <textarea
                              value={formData.notes_verification}
                              onChange={(e) => setFormData({ ...formData, notes_verification: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_verification_notes')}
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_general_notes')}</label>
                            <textarea
                              value={formData.notes}
                              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                              rows={2}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                              placeholder={p('placeholder_general_notes')}
                            />
                          </div>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}

      {showViewModal && selectedBalance ? ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => { setShowViewModal(false); setSelectedBalance(null); }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Scale className="h-6 w-6 text-purple-400" />
                {p('view_modal_title')}
              </h2>
              <button onClick={() => { setShowViewModal(false); setSelectedBalance(null); }} className="text-white/60 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Section 1: Informations Balance */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Scale size={18} className="text-sky-400" />
                      <h3 className="text-lg font-semibold text-white">{p('section_balance_info')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_reference')}</p>
                    <p className="text-white font-semibold">{selectedBalance.balance_ref}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_fiscal_year')}</p>
                    <p className="text-white font-semibold">{selectedBalance.exercice_fiscal}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_balance_type')}</p>
                    <p className="text-white font-semibold">{translateBalanceType(selectedBalance.type_balance)}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_status')}</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      selectedBalance.statut_balance === 'Validé' ? 'bg-green-500/20 text-green-400' :
                      selectedBalance.statut_balance === 'Définitif' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {translateBalanceStatus(selectedBalance.statut_balance)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Section 2: Soldes & Mouvements */}
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Activity size={18} className="text-amber-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_balances_movements')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_debit_end')}</p>
                    <p className="text-green-400 font-bold">{fmtMoney(parseFloat(selectedBalance.solde_debiteur_fin || 0))}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_credit_end')}</p>
                    <p className="text-blue-400 font-bold">{fmtMoney(parseFloat(selectedBalance.solde_crediteur_fin || 0))}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_net_balance')}</p>
                    <p className={`font-bold ${parseFloat(selectedBalance.solde_net) > 0 ? 'text-green-400' : parseFloat(selectedBalance.solde_net) < 0 ? 'text-red-400' : 'text-white'}`}>
                      {fmtMoney(parseFloat(selectedBalance.solde_net || 0))}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Grid for remaining sections */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Section 3: Contrôles & Rapprochements */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <CheckCircle size={18} className="text-emerald-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_controls_short')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_accounting_validation')}</p>
                      <p className="text-white font-semibold">{selectedBalance.validation_comptable ? p('yes') : p('no')}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('view_label_pcn_compliance')}</p>
                      <p className="text-white font-semibold">{selectedBalance.conformite_pcn ? p('yes') : p('no')}</p>
                    </div>
                  </div>
                </div>
                
                {/* Section 4: Commentaires & Notes */}
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-violet-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_notes')}</h3>
                  </div>
                  <div className="space-y-3">
                    {selectedBalance.notes && (
                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p('label_general_notes')}</p>
                        <p className="text-white/70 text-sm italic">{selectedBalance.notes}</p>
                      </div>
                    )}
                    {!selectedBalance.notes && (
                      <p className="text-white/40 text-sm italic">{p('no_notes_available')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => { setShowViewModal(false); setSelectedBalance(null); }}
              className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
            >
              {p('close')}
            </button>
          </motion.div>
        </div>,
        document.body
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default BalanceGeneralePage;

